import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ReportsAPI } from '../../api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContainer = styled.div`
  background-color: var(--background-card);
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh; /* Limit height to 90% of viewport height */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 480px) {
    width: 95%;
    max-height: 85vh; /* Slightly smaller on mobile */
    margin: 0 auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: var(--text-dim);
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  
  &:hover {
    color: white;
  }
`;

const ModalHeader = styled.div`
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid var(--glass-border);
  
  h2 {
    font-size: 24px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    
    svg {
      color: var(--error-color);
    }
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  max-height: calc(90vh - 70px); /* Account for header height */
  overflow-y: auto;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    max-height: calc(85vh - 60px); /* Adjust for smaller header on mobile */
    font-size: 0.95em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--glass-border);
  color: white;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  option {
    background-color: var(--background-card);
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--glass-border);
  color: white;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &::placeholder {
    color: var(--text-dim);
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 14px;
    min-height: 80px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  padding: 10px;
  background-color: rgba(229, 62, 62, 0.1);
  border-radius: 5px;
  margin-top: 15px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  padding: 10px;
  background-color: rgba(72, 187, 120, 0.1);
  border-radius: 5px;
  margin-top: 15px;
  font-size: 14px;
`;

const ContentInfo = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  
  p {
    margin: 5px 0;
    font-size: 14px;
    color: var(--text-dim);
    
    strong {
      color: white;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-bottom: 10px;
    
    p {
      font-size: 13px;
      margin: 3px 0;
    }
  }
`;

const ReportModal = ({ isOpen, onClose, contentId, contentType, contentTitle, sourceUrl }) => {
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Reset form when modal closes or opens
  useEffect(() => {
    if (isOpen) {
      // Reset the form state when modal opens
      setSelectedReason('');
      setCustomMessage('');
      setError('');
      setSuccess(false);
      
      // Fetch reasons when modal opens
      const fetchReasons = async () => {
        try {
          const response = await ReportsAPI.getReportReasons();
          
          // Check if the data has the new format (array of objects with value/label)
          const reasonsData = response.data.data || {};
          
          // Handle both the new format (reasons array inside data) and the old format
          let formattedReasons = [];
          if (Array.isArray(reasonsData.reasons)) {
            // New format - array of objects with value and label
            formattedReasons = reasonsData.reasons;
            console.log("Using new reasons format:", formattedReasons);
          } else if (Array.isArray(reasonsData)) {
            // Old format - array of strings
            formattedReasons = reasonsData.map(reason => ({ 
              value: reason, 
              label: reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            }));
            console.log("Using old reasons format:", formattedReasons);
          }
          
          setReasons(formattedReasons);
          if (formattedReasons.length > 0) {
            setSelectedReason(formattedReasons[0].value);
          }
        } catch (err) {
          console.error('Error fetching report reasons:', err);
          setError('Failed to load report options. Please try again later.');
          setReasons([]);
        }
      };
      
      fetchReasons();
    }
  }, [isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedReason) {
      setError('Please select a reason for your report');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Ensure content_type is either 'movie' or 'show' as required by the API
      let normalizedContentType = contentType;
      if (contentType === 'episode') {
        normalizedContentType = 'show';
      } else if (contentType !== 'movie' && contentType !== 'show') {
        normalizedContentType = 'show'; // Default to 'show' if unknown type
      }
      
      // Always include episode information in the custom message
      let finalCustomMessage = customMessage;
      if (contentTitle && contentTitle.includes(' - ')) {
        // Extract episode information
        const episodeInfo = contentTitle.split(' - ')[1] || '';
        
        // If the user didn't provide any message, create one with the episode info
        if (!customMessage || customMessage.trim() === '') {
          finalCustomMessage = `Reporting issue with: ${contentTitle}`;
        } 
        // If they provided a message but didn't mention the episode, add it
        else if (!customMessage.includes(episodeInfo)) {
          finalCustomMessage = `${customMessage}\n\nEpisode: ${episodeInfo}`;
        }
      }
      
      const reportData = {
        content_id: contentId,
        content_type: normalizedContentType,
        reason: selectedReason,
        custom_message: finalCustomMessage,
        source_url: sourceUrl
      };
      
      console.log("Submitting report with data:", reportData);
      const response = await ReportsAPI.createReport(reportData);
      console.log("Report submission response:", response.data);
      
      // Reset form fields after successful submission
      setSelectedReason('');
      setCustomMessage('');
      setSuccess(true);
      
      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting report:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.isRateLimit) {
        setError("You've reached the rate limit. Please wait a moment before trying again.");
      } else {
        // Handle the specific "Show not found" error
        if (err.response?.data?.message === 'Show not found') {
          setError('Unable to submit report: The show information could not be found. Please try again later or contact support.');
        } else {
          const errorMessage = err.response?.data?.message || err.response?.data?.detail?.message || 'Failed to submit report. Please try again.';
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        
        <ModalHeader>
          <h2>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Report Issue
          </h2>
        </ModalHeader>
        
        <ModalBody>
          <ContentInfo>
            <p><strong>Content:</strong> {contentTitle}</p>
            <p><strong>Type:</strong> {contentType === 'movie' ? 'Movie' : 'TV Show'}</p>
            {sourceUrl && <p><strong>Source:</strong> Current source</p>}
          </ContentInfo>
          
          {success ? (
            <SuccessMessage>
              Thank you for your report. We will review it as soon as possible.
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="reason">Issue Type</Label>
                <Select 
                  id="reason" 
                  value={selectedReason} 
                  onChange={e => setSelectedReason(e.target.value)}
                  required
                >
                  <option value="">Select an issue</option>
                  {Array.isArray(reasons) && reasons.map(reason => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">Additional Details (optional)</Label>
                <TextArea 
                  id="message" 
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Please provide any additional information that might help us resolve the issue..."
                />
              </FormGroup>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <SubmitButton type="submit" disabled={loading || !selectedReason}>
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Submit Report"}
              </SubmitButton>
            </Form>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ReportModal; 